/* assets/vc-cart.js — window.vcCart and <vc-cart-drawer> (task-9-brief.md Step 1; the drawer's markup is
 * sections/vc-cart-drawer.liquid).
 *
 * API (task-9-brief.md "Produces"; used by Tasks 12, 13 and 14):
 *   vcCart.add(items: [{id, quantity, properties}], { open = true }?) -> Promise<cart>
 *                                                                        rejects with an Error whose message is shown
 *   vcCart.refresh() -> Promise<cart>                                    in the drawer (or, with open: false, by the caller)
 *   vcCart.open(), vcCart.close()
 * `open: false` (Task 13b) adds without opening the bag: the product page presses its wax seal onto the "Reserved for you"
 * slip instead and settles it beside the header's Bag. Everything else is the same (the cart and the drawer's lines are
 * refreshed, `vc:cart:changed` and `vc:cart:added` fire); any other value, or no options, opens the bag as before.
 * Events on `document`: `vc:cart:changed` {count, cart} after every change (add, quantity, remove, refresh);
 * `vc:cart:added` {items} after add(), `items` being the line items /cart/add.js returned.
 * NB `quantity` there is each line's total after the add (2 if an identical item was already in the bag), not the amount added.
 *
 * Ported from D:/mrs-studios-shopify/theme/assets/mrs-cart.js:
 *   - addToCart (mrs-cart.js:126-139): POST /cart/add.js as JSON; on a 422, Shopify's `description` is the
 *     customer-facing reason and is what the drawer shows;
 *   - changeQuantity (mrs-cart.js:141-150): POST /cart/change.js {id: line key, quantity}; a refused change returns an
 *     error body, not a cart, so the cart is re-read;
 *   - MrsCart.add (mrs-cart.js:176-185): add, re-read /cart.js, render, open the drawer, resolve with the cart.
 * Changed from MRS: the drawer is re-rendered server-side through the Section Rendering API instead of being built
 * as a JS string (the brief), requests run one at a time, and the dialog is modal as the header drawer is.
 *
 * ref: https://shopify.dev/docs/api/ajax/reference/cart  (add.js {items}, cart.js, change.js {id: key}, 422 shape,
 *      window.Shopify.routes.root for locale-aware URLs)
 * ref: https://shopify.dev/docs/api/ajax/section-rendering  (`?sections=<id>`; a static section's id is its file name;
 *      a section that fails to render comes back as null)
 * The cart page (Task 19, sections/vc-cart.liquid) is a second view of the same bag: <vc-cart-page> shares the drawer's line handling (quantity
 * and Remove by line key, the Section Rendering swap, focus kept), and every change re-renders both views in one request
 * (`?sections=vc-cart-drawer,<the page's section id>`, the id being printed in its data-section-id: a JSON template's section id is generated).
 * That read is sent WITHOUT an Accept header (Task 20, Part 2): Shopify chooses the format of a storefront URL by it, and `Accept: application/json`
 * alone makes /cart answer with the cart as JSON (status 200, no section in it) and a collection URL with a 404. The page then rendered nothing,
 * and said nothing: Remove changed the server's cart and the page went on showing the old one. A page that still cannot be re-rendered after a
 * visitor's own change (its section is not in the answer) is reloaded once (renderViews), so it never stays on a bag that is gone.
 * On that page the header's Bag link is just a link to it: no drawer opens over a page that shows the same bag.
 * Dialog behaviour (focus into the sheet, Tab contained, Esc closes, focus returns to the opener):
 *      https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/ — the same as vc-header.js:164-222, whose scroll lock
 *      (the root element's inline overflow, saved and restored) is used here unchanged. The two locks never nest:
 *      opening the bag first closes the header drawer, and while the bag is open its scrim and focus trap keep the
 *      header's Menu out of reach.
 */
const SECTION = 'vc-cart-drawer';
const SHEET_OUT_MS = 700; // = the .vc-cart__sheet closing transition (vc-cart-drawer.liquid stylesheet)
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
const MSG_ADD = 'This piece could not be added to your bag. Please try again.';
const MSG_CHANGE = 'Your bag could not be updated. Please try again.';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const route = (path) => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + path;
const drawer = () => document.querySelector('vc-cart-drawer');
const pageView = () => document.querySelector('vc-cart-page');
const shown = (el) => !!(el && el.isConnected && el.getClientRects().length);
const emit = (type, detail) => document.dispatchEvent(new CustomEvent(type, { detail }));

async function readJson(res) {
  try { return await res.json(); } catch (_) { return {}; }
}

/* A 422 carries Shopify's customer-facing reason (mrs-cart.js:132-134); anything else gets the house line. */
function failure(res, data, fallback) {
  const said = res && res.status === 422 && data && typeof data.description === 'string' ? data.description.trim() : '';
  const err = new Error(said || fallback);
  err.status = res ? res.status : 0;
  err.body = data || null;
  return err;
}

async function getCart() {
  const res = await fetch(route('cart.js'), { headers: { Accept: 'application/json' } });
  if (!res.ok) throw failure(res, null, MSG_CHANGE);
  return res.json();
}

/* The HTML of the sections that show the bag, {section id: html}: the drawer's, and on the cart page its own. Null when the request failed
   (the lines then stay as they were); a section that could not be rendered is null in it. */
async function getSections() {
  const ids = [SECTION];
  const page = pageView();
  if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
  try {
    // a JSON template's section renders on the page that uses the template, so the cart page is asked at its own URL. No Accept header (see the
    // top of this file: `Accept: application/json` alone gets the cart's JSON from /cart, not the sections), and never from the browser's cache.
    const res = await fetch(route(`${page ? 'cart' : ''}?sections=${ids.join(',')}`), { cache: 'no-store' });
    if (!res.ok) return null;
    const sections = await res.json();
    return sections && typeof sections === 'object' ? sections : null;
  } catch (_) {
    return null;
  }
}

/* Re-render the views from `sections`. `reload`: after a visitor's own change, a cart page that could not be re-rendered (its section is not in
   the answer, or the answer has no lines to swap) would go on showing a bag that is gone, on the page where the money is read: load it again and
   the server renders it. No loop: only the click that made the change passes `reload`, and loading a page calls nothing here. */
function renderViews(sections, { reload = false } = {}) {
  const d = drawer();
  if (d) d.render(sections && sections[SECTION]);
  const page = pageView();
  if (page && !page.render(sections && sections[page.dataset.sectionId]) && reload) window.location.reload();
}

function showErrorInViews(message) {
  for (const v of [drawer(), pageView()]) if (v) v.showError(message);
}

/* One request at a time: a quantity click never races an add or another click. */
let chain = Promise.resolve();
function queue(task) {
  const run = chain.then(task);
  chain = run.catch(() => {});
  return run;
}

async function refreshNow() {
  const [cart, sections] = await Promise.all([getCart(), getSections()]);
  renderViews(sections);
  emit('vc:cart:changed', { count: cart.item_count, cart });
  return cart;
}

/* `open: false` (the product page plays its wax seal instead, Task 13b) leaves the bag shut: the cart, the drawer's lines and the
   events are updated just the same, and a refused add re-reads the bag (M5) and rejects, with the reason in the Error: the caller says it. */
async function addNow(items, { open = true } = {}) {
  const d = drawer();
  if (d) d.showError('');
  let res = null;
  let data = null;
  try {
    res = await fetch(route('cart/add.js'), { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ items }) });
    data = await readJson(res);
  } catch (_) { /* network: res stays null */ }
  if (!res || !res.ok) {
    const err = failure(res, data, MSG_ADD);
    // A multi-item add can be refused after some of its lines went in: re-read what the bag really holds, so the header's count and the
    // drawer's lines are not stale (batch review 1, M5; changeNow does the same, mrs-cart.js:147-148). Not vcCart.refresh(): this runs
    // inside the queue, and a queued task would wait for itself.
    await refreshNow().catch(() => {});
    if (d && open) { d.showError(err.message); d.open(); }
    throw err;
  }
  const cart = await refreshNow();
  emit('vc:cart:added', { items: data.items || [] });
  if (d && open) d.open();
  return cart;
}

async function changeNow(key, quantity) {
  let res = null;
  let data = null;
  try {
    res = await fetch(route('cart/change.js'), { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ id: key, quantity }) });
    data = await readJson(res);
  } catch (_) { /* network */ }
  if (!res || !res.ok) {
    const err = failure(res, data, MSG_CHANGE);
    await refreshNow().catch(() => {}); // re-read what the cart really holds (mrs-cart.js:147-148)
    showErrorInViews(err.message);
    throw err;
  }
  renderViews(await getSections(), { reload: true });
  emit('vc:cart:changed', { count: data.item_count, cart: data });
  return data;
}

/* What the drawer and the cart page share (Task 19): a view of the bag, its lines carrying [data-vc-line] and [data-vc-act] buttons, its body
   ([data-vc-cart-body]) and its [data-vc-cart-sync] / [data-vc-cart-toggle] parts re-rendered from the Section Rendering API. */
class VcCartView extends HTMLElement {
  bind() {
    this.errorEl = this.querySelector('[data-vc-cart-error]');
    this.pending = new Set(); // line keys with a change in flight
    this.ac = new AbortController();
    this.addEventListener('click', (e) => this.onClick(e), { signal: this.ac.signal });
  }

  unbind() {
    if (this.ac) this.ac.abort();
  }

  body() { return this.querySelector('[data-vc-cart-body]'); }

  /* ---------- quantity and remove, by line key ---------- */
  onClick(e) {
    const btn = e.target.closest('[data-vc-act]');
    if (!btn || btn.disabled) return;
    const line = btn.closest('[data-vc-line]');
    const qty = Number(btn.dataset.vcQty);
    if (!line || !line.dataset.key || !Number.isInteger(qty) || qty < 0) return;
    // A line with a change in flight ignores clicks: its buttons still carry the old quantities, and a second
    // Remove would post a key that is already gone (400 "no valid id or line parameter"), which read as an error
    // after a removal that had worked. Kept per key, so it holds across a re-render by another line's change.
    const key = line.dataset.key;
    if (line.classList.contains('is-pending') || this.pending.has(key)) return;
    this.pending.add(key);
    this.showError('');
    this.markPending();
    queue(() => changeNow(key, qty))
      .catch(() => {}) // shown in the views by changeNow
      .finally(() => {
        this.pending.delete(key);
        line.classList.remove('is-pending'); // the clicked element, if a re-render has not replaced it
        this.markPending();
      });
  }

  // The lines and the list reflect the keys in flight: `is-pending` per line, aria-busy on the list.
  markPending() {
    const body = this.body();
    if (!body || !this.pending) return;
    body.querySelectorAll('[data-vc-line]').forEach((l) => l.classList.toggle('is-pending', this.pending.has(l.dataset.key)));
    if (this.pending.size) body.setAttribute('aria-busy', 'true');
    else body.removeAttribute('aria-busy');
  }

  /* ---------- render: swap the lines, sync the summary in place ---------- */
  // true when the view now shows this rendering; false when there was nothing to swap (no HTML, or none with the lines in it)
  render(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-vc-cart-body]');
    const body = this.body();
    if (!fresh || !body) return false;
    const mark = this.focusMark(body);
    const top = body.scrollTop;
    body.replaceWith(fresh);
    // #vc-cart-drawer is the scroll container, so the swap would reset the list to its top. Keep the reader's place:
    // Safari (macOS, iOS) and Firefox on macOS do not focus a clicked <button>, so there is no focus to restore.
    fresh.scrollTop = top;
    this.markPending();
    this.querySelectorAll('[data-vc-cart-sync]').forEach((el) => {
      const src = doc.querySelector(`[data-vc-cart-sync="${el.dataset.vcCartSync}"]`);
      if (src && el.innerHTML !== src.innerHTML) el.innerHTML = src.innerHTML; // the subtotal's aria-live speaks here
    });
    this.querySelectorAll('[data-vc-cart-toggle]').forEach((el) => {
      const src = doc.querySelector(`[data-vc-cart-toggle="${el.dataset.vcCartToggle}"]`);
      if (src) el.hidden = src.hidden;
    });
    if (mark) this.focusRestore(mark);
    return true;
  }

  // Where focus was inside the lines: the line (key, position) and the control.
  focusMark(body) {
    const active = document.activeElement;
    if (!active || !body.contains(active)) return null;
    const lines = Array.from(body.querySelectorAll('[data-vc-line]'));
    const line = active.closest('[data-vc-line]');
    return { key: line ? line.dataset.key : null, index: Math.max(0, lines.indexOf(line)), act: active.dataset.vcAct || null };
  }

  // Same control on the same line; a "fewer" that is now disabled gives way to "more"; a removed line hands focus to
  // the line that took its place; an empty bag focuses its message.
  focusRestore({ key, index, act }) {
    const body = this.body();
    const lines = Array.from(body.querySelectorAll('[data-vc-line]'));
    const line = lines.find((l) => l.dataset.key === key);
    let target = null;
    if (line) {
      const pick = (a) => line.querySelector(`[data-vc-act="${a}"]:not([disabled])`);
      target = (act && pick(act)) || pick('more') || line.querySelector('.vc-cart__name');
    } else if (lines.length) {
      target = lines[Math.min(index, lines.length - 1)].querySelector('.vc-cart__name');
    } else {
      target = body.querySelector('[data-vc-cart-empty]');
    }
    if (target) target.focus({ preventScroll: false });
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }
}

export class VcCartDrawer extends VcCartView {
  connectedCallback() {
    this.sheet = this.querySelector('[data-vc-cart-sheet]');
    this.errorEl = this.querySelector('[data-vc-cart-error]');
    if (!this.sheet) return;
    this.isOpen = false;
    this.bind();
    const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.ac.signal });
    on(document, 'keydown', (e) => this.onKey(e));
    // The Bag link stays a plain /cart link; with this module it opens a dialog instead, except on the cart page, where it is a link to the page
    // (it opens nothing over a page that shows the same bag: see the click handler below), so it does not announce a dialog.
    if (!pageView()) document.querySelectorAll('[data-vc-bag]').forEach((a) => a.setAttribute('aria-haspopup', 'dialog'));
  }

  disconnectedCallback() {
    this.unbind();
    clearTimeout(this.hideT);
    this.unlockScroll();
  }

  onClick(e) {
    if (e.target.closest('[data-vc-cart-close]')) { this.close(); return; }
    super.onClick(e);
  }

  /* ---------- open / close ---------- */
  open() {
    if (!this.sheet || this.isOpen) return;
    const active = document.activeElement;
    this.opener = active && active !== document.body && !this.contains(active) ? active : null;
    const header = document.querySelector('vc-header');
    if (header && typeof header.closeDrawer === 'function') header.closeDrawer({ instant: true, restoreFocus: false });
    if (header && typeof header.closeAll === 'function') header.closeAll(true);
    this.isOpen = true;
    clearTimeout(this.hideT);
    this.hidden = false;
    void this.offsetWidth; // commit display before the class change, so the sheet slides
    this.classList.add('is-open');
    this.lockScroll();
    // Focus the dialog's title (tabindex -1), not Close: the name is read first and Tab then reaches Close
    // (APG dialog: "the most appropriate focus placement depends on the nature and size of the content").
    const title = this.querySelector('[data-vc-cart-title]');
    if (title) title.focus({ preventScroll: true });
  }

  close({ instant = false, restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.unlockScroll();
    this.showError('');
    const done = () => { if (!this.isOpen) this.hidden = true; };
    clearTimeout(this.hideT);
    if (instant || reducedMotion()) done();
    else this.hideT = setTimeout(done, SHEET_OUT_MS);
    if (restoreFocus) {
      const back = shown(this.opener) ? this.opener : Array.from(document.querySelectorAll('[data-vc-bag]')).find(shown);
      if (back && back.focus) back.focus({ preventScroll: true });
    }
    this.opener = null;
  }

  lockScroll() {
    if (this.lockedOverflow !== undefined) return;
    this.lockedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (this.lockedOverflow === undefined) return;
    document.documentElement.style.overflow = this.lockedOverflow;
    this.lockedOverflow = undefined;
  }

  focusables() {
    return Array.from(this.sheet.querySelectorAll(FOCUSABLE)).filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
  }

  onKey(e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const f = this.focusables();
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    const active = document.activeElement;
    // Outside the sheet, or on a tabindex -1 target inside it (the title, the empty message): wrap explicitly.
    if (!this.sheet.contains(active) || !f.includes(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }
}

/* The cart page (Task 19, sections/vc-cart.liquid): the same bag as a page. Nothing of its own but the element: the base class does the rest. */
export class VcCartPage extends VcCartView {
  connectedCallback() { this.bind(); }

  disconnectedCallback() { this.unbind(); }
}

if (!customElements.get('vc-cart-drawer')) customElements.define('vc-cart-drawer', VcCartDrawer);
if (!customElements.get('vc-cart-page')) customElements.define('vc-cart-page', VcCartPage);

if (!window.vcCart) {
  window.vcCart = {
    add(items, options) { return queue(() => addNow(Array.isArray(items) ? items : [items], { open: !options || options.open !== false })); },
    refresh() { return queue(refreshNow); },
    open() { const d = drawer(); if (d) d.open(); },
    close() { const d = drawer(); if (d) d.close(); },
  };

  // The header's Bag link (sections/vc-header.liquid `data-vc-bag`) opens the drawer; without this module it is a
  // plain link to /cart. Modified clicks (new tab / window) keep the link's own behaviour.
  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('a[data-vc-bag]') : null;
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (pageView()) return; // the cart page shows the bag itself: the link is a link to it
    const d = drawer();
    if (!d || !d.sheet) return;
    e.preventDefault();
    d.open();
  });

  // Back/forward cache: never restore an open, locked drawer, and bring the count up to date.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    const d = drawer();
    if (d && d.close) d.close({ instant: true, restoreFocus: false });
    window.vcCart.refresh().catch(() => {});
  });
}
